import React, { useEffect, useState } from 'react';
import { formatDateOrDash } from "../../../components/Utils/Dates";
import Card from '../../../components/Cards/Card';
import BootstrapTable from 'react-bootstrap-table-next';
import { Col, Row } from 'react-bootstrap';
import paginationFactory from 'react-bootstrap-table2-paginator';
import Loader from '../../../components/Common/Loader';
import { useTranslation } from 'react-i18next';
import useReleases from '../Hooks/useReleases';
import { PaginationRequest } from '../../../api/Common/Util';
import moment from 'moment';
import useItem from '../Hooks/useItem';
import { Field } from '../../../components/Common/Field/Field';
import { useModalHook } from '../../../components/Modals/useModalContext';
import { ReleaseStockModalBuilder } from '../Shared/ReleaseStockModal';
import { Tooltip } from '../../../components/Utils/Tooltips';
import { More } from '../../../components/Icons/Icons';
import { EnumValue } from '../../../api/Freezer/Entities/Utils';
import SearchHeader from '../../../components/Common/SearchHeader';
import { useFormModal } from '../../../components/Modals/formModal.js/FormModal';
import InventoryItemMovementHistoryList from '../Shared/InventoryItemMovementHistoryList';
import { AssignmentType } from '../../../api/Inventory/Entities/InventoryEntities';

function parseOriginToAssignmentType(origin) {
    switch (origin) {
        case 'pharmacy':
            return AssignmentType.General.Name;
        case 'protocol':
            return AssignmentType.Protocol.Name;
        case 'laboratorykit':
            return AssignmentType.ProtocolLaboratoryKit.Name;
        default:
            throw new Error(`Unknown origin: ${origin}`);
    }
}

export default function InventoryItemDetailPage({ match: { params } }) {
    const { t } = useTranslation();
    const { itemId, origin } = params
    const [openStockModal, StockModal] = useModalHook(ReleaseStockModalBuilder({ assignmentType: parseOriginToAssignmentType(origin) }))
    const [item, isItemLoading] = useItem({ itemId: itemId, includeStock: true, includeLastEntry: true, assignmentType: parseOriginToAssignmentType(origin) });
    const [HistoryModal, HistoryActions] = useFormModal({ FormContent: InventoryItemMovementHistoryList, size: 'lg' })
    const [pagedReleases, isReleaseLoading, navigation] = useReleases({
        preload: true,
        requestProps: {
            ... new PaginationRequest({
                pageNumber: 1,
                pageSize: 10,
                sortColumn: 'lotNumber',
            }),
            includeExpired: false,
            includeStock: true,
            assignmentType: parseOriginToAssignmentType(origin),
            itemId: itemId
        }
    })

    const columns = [
        { dataField: 'lotNumber', text: t('inventory.commons.lotNumber'), headerStyle: { width: '7rem' } },
        {
            text: t('commons.expirationDate'), headerStyle: { width: '10rem' }
            , formatter: function (_cell, release, rowIndex) {
                return moment(release.expirationDate).format("DD/MM/YYYY");
            }
        },
        {
            text: t('inventory.commons.stock')
            , headerStyle: { width: '5rem' }
            , formatter: function (_cell, release, rowIndex) {
                return release.stockQuantity
            }
        },
        {
            text: t('commons.created')
            , headerStyle: { width: '5rem' }
            , formatter: function (_cell, release, rowIndex) {
                return moment(release.createAt).format("DD/MM/YYYY")
            }
        },
        {
            dataField: 'view',
            text: "",
            isDummyField: true,
            headerClasses: 'header-actions',
            classes: 'row-actions',
            formatter: function (_cell, row, rowIndex) {
                return (
                    <div key={rowIndex}>
                        <button className="btn-table-actions" onClick={() => { openStockModal(row.id) }}>
                            <Tooltip tooltip={t('commons.viewDetails')} id={"tooltipedit-" + row.Id}>
                                <span aria-hidden="true"><More /></span>
                            </Tooltip>
                        </button>
                    </div>
                )
            }
        }
    ]



    function handleTableChange(type, { page, sizePerPage, sortField, sortOrder }) {
        navigation.ToPage({
            page
        })
    }

    const All = [
        new EnumValue(1, 'onlyAvailable'),
        new EnumValue(0, 'all'),
    ]
    const inputs = [
        { type: 'select', title: t('commons.type'), name: 'onlyAvailable', options: All, optionName: (op) => t(`inventory.stock.release.${op.Name}`), onChange: (e, v) => { }, disabled: false, value: null, col: { sm: 12, md: 3, lg: 3 } },
    ]
    const handleSearch = async (filter) => {
        const { onlyAvailable } = filter

        await navigation.Search({
            ... new PaginationRequest({
                pageNumber: 1,
                pageSize: 10,
                sortColumn: 'lotNumber',
            }),
            includeExpired: onlyAvailable.Name != 'all' ? false : true,
            includeStock: true,
            itemId: itemId
        })
    }
    const handleShowHistory = async () => {
        HistoryActions.Show({
            title: t('inventory.movements.commons.historyOfChanges'),
            initValue: { itemId: itemId }
        })
    }

    return (
        <>
            <Card
                header={<div>{t('inventory.commons.medicationDetail')}</div>}
                content={<div>
                    <Loader isLoading={isItemLoading}>
                        <Row>
                            <Col md={4} sm={12}>
                                <div className="info" style={{ justifyContent: 'space-between!important' }}>
                                    <Field title={'ID'} value={item.id} spaceType={'between'} />
                                    <Field title={t('commons.description')} spaceType={'between'} value={item.presentation} />
                                </div>
                            </Col>
                            <Col md={4} sm={12}>
                                <div className="info" style={{ justifyContent: 'space-between!important' }}>
                                    <Field title={t('commons.externalId')} value={item.externalId} spaceType={'between'} />
                                    <Field title={t('commons.type')} spaceType={'between'} value={item.type} />
                                </div>
                            </Col>
                            <Col md={4} sm={12}>
                                <div className="info" style={{ justifyContent: 'space-between!important' }}>
                                    <Field title={t('inventory.commons.totalStock')} value={item.stockQuantity} spaceType={'between'} />
                                    <Field title={t('commons.created')} spaceType={'between'} value={moment(item.createAt).format("DD/MM/YYYY")} />
                                </div>
                            </Col>
                            <Col md={4} sm={12}>
                                <div className="info" style={{ justifyContent: 'space-between!important' }}>
                                    <Field title={t('inventory.movements.commons.lastEntry')} value={formatDateOrDash(item.lastEntry, "DD/MM/YYYY HH:mm")} spaceType={'between'} />
                                </div>
                            </Col>
                            {
                                origin.toLowerCase() == 'pharmacy' && (<Col md={4} sm={12}>
                                    <div className="info" style={{ justifyContent: 'space-between!important' }}>
                                        <div>
                                            <button className="btn btn-secondary" onClick={handleShowHistory}>{t('inventory.movements.commons.historyOfMovements')}</button>
                                        </div>
                                    </div>
                                </Col>)
                            }
                        </Row>
                        <hr></hr>
                        <div>
                            <SearchHeader inputs={inputs} handleSearch={handleSearch} init={{ onlyAvailable: new EnumValue(1, 'onlyAvailable') }} buttonTitle={t('buttons.apply')} />
                        </div>
                        <Loader isLoading={isReleaseLoading}>
                            <BootstrapTable
                                bordered={false}
                                hover
                                remote
                                condensed
                                keyField='id'
                                classes="table-spaced table-spaced-small"
                                wrapperClasses="table-responsive"

                                pagination={paginationFactory({
                                    page: pagedReleases.PageNumber,
                                    sizePerPage: pagedReleases.PageSize,
                                    totalSize: pagedReleases.TotalCount,
                                    hideSizePerPage: true
                                })}

                                onTableChange={handleTableChange}

                                data={pagedReleases.Values}
                                columns={columns}
                                loading={isReleaseLoading}
                                noDataIndication={() => <>{t("commons.noData")}</>}
                            />
                        </Loader>
                    </Loader>
                </div>}
            />
            <StockModal />
            <HistoryModal />
        </>
    )
}